import React from 'react';
import { Layout, Button, Tooltip, theme } from 'antd';
import { MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { IconShieldCog } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import UserProfile from '../UserProfile';
import AppLogo from '../AppLogo';
import NotificationBell from '../NotificationBell';
import HeavyDutyMachineryNavMenu from './HeavyDutyMachineryNavMenu';
import HostToolsMenu from './HostToolsMenu';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { useScrollChrome } from '../../hooks/useScrollChrome';

const { Header } = Layout;

/**
 * Single header shared by every authenticated page (see AppLayout). "List
 * your space" is the one and only entry point into becoming a host: if the
 * signed-in user has no HostProfile yet it opens the onboarding wizard's own
 * page, never a modal; once they have one, it goes straight to publishing a
 * new space.
 *
 * On isMobile this collapses to just the logo + notification bell -- the
 * hamburger/Drawer combo it used to open (nav menu, Settings/Logout,
 * language picker, "List your space", and the "+" add-menu for every one of
 * the host's own catalogs) is retired there in favor of MobileBottomNav (see
 * AppLayout), including the "+" itself -- see that component's own doc
 * comment for why it now lives there instead of here. Desktop/tablet
 * (isDesktopNav below) keep the original header exactly as it was.
 *
 * The header itself never moves or hides -- it stays fixed exactly where it
 * always is. The only scroll reaction here is a soft bottom-edge shadow
 * once the page has scrolled at all (see useScrollChrome), giving the
 * impression that the page's own content is sliding underneath a header
 * that's floating above it. Hiding on scroll-down/showing on scroll-up is
 * MobileBottomNav's behavior instead, not this one's.
 */
const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const { hasHostProfile, loading: hostProfileLoading } = useHostProfile();
  const { isAdminOrModerator } = useAuth();
  const { scrolled } = useScrollChrome();

  const goToListYourSpace = () => {
    navigate(hasHostProfile ? '/heavy-duty-machinery/new' : '/host/onboarding');
  };

  if (isMobile) {
    return (
      <Header
        style={{
          position: 'fixed',
          width: '100%',
          zIndex: 1000,
          background: token.colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          boxShadow: scrolled ? '0 2px 10px rgba(0,0,0,0.08)' : 'none',
          transition: 'box-shadow 200ms ease',
        }}
      >
        <AppLogo />
        <NotificationBell />
      </Header>
    );
  }

  return (
    <Header
      style={{
        position: 'fixed',
        width: '100%',
        zIndex: 1000,
        background: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 16px',
      }}
    >
      <AppLogo />

      <HeavyDutyMachineryNavMenu mode="horizontal" />

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <Button
          type="primary"
          shape="round"
          icon={<PlusOutlined />}
          loading={hostProfileLoading}
          onClick={goToListYourSpace}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_CREATE_SPACE)}
        </Button>
        {/* Right after "Publicar maquinaria" and before the messages icon:
            everything a host manages, behind one icon (see HostToolsMenu). */}
        <HostToolsMenu />
        <Tooltip title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_NAV_BUTTON)}>
          <Button
            type="text"
            icon={<MessageOutlined style={{ fontSize: 18 }} />}
            onClick={() => navigate('/heavy-duty-machinery/messages')}
          />
        </Tooltip>
        {isAdminOrModerator && (
          <Tooltip title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_ADMIN_BUTTON)}>
            <Button
              type="text"
              icon={<IconShieldCog size={18} />}
              onClick={() => navigate('/admin')}
            />
          </Tooltip>
        )}
        <NotificationBell />
        <LanguageSwitcher />
        <UserProfile />
      </div>
    </Header>
  );
};

export default AppHeader;
